'use client'

import Link from 'next/link'
import { Fragment, useState } from 'react'

import { Image } from '@/components/Image'
import { SectionContent } from '@/components/SectionContent'
import { SectionHeader } from '@/components/SectionHeader'
import { ServiceModal } from '@/components/ServiceModal'

export default function Page() {
  const [selectedService, setSelectedService] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const services = [
    {
      title: '法人税制対応',
      icon: 'apartment',
      text: [
        '法人税務は企業にとって避けて通れない重要な業務です。',
        '私たちは、毎期の申告書作成・チェックを通じて「正確さ」と「安心感」をお届けします。経営に集中できる環境を整えることが、私たちの使命です。',
      ],
      contents: [
        {
          title: '税務申告書の作成・レビュー',
          text: '膨大な数値や添付書類を正確に整え、安心できる申告を実現します。',
        },
        {
          title: '税務調査対応',
          text: '調査時には、事前準備から当日の立会い、調査後の対応までフルサポート。お客様が不安を感じることなく対応できる体制を整えています。',
        },
        {
          title: '組織再編税制・グループ法人税制',
          text: '合併・分割・持株会社設立など、複雑な税制にも最新知識で対応。グループ経営を見据えた中長期的な税務戦略をご提案します。',
        },
      ],
    },
    {
      title: 'バックオフィスサポート',
      icon: 'domain',
      text: [
        '日々の経理や人事労務は企業活動を支える基盤ですが、手間と時間がかかり本業を圧迫することも少なくありません。',
        '私たちは、その負担を軽減し、効率的で安心なバックオフィス体制を構築します。',
      ],
      contents: [
        {
          title: '記帳代行（主要クラウド会計ソフト対応）',
          text: 'freee、マネーフォワード、弥生会計など、主要クラウド会計ソフトに対応。データ入力や帳簿作成を代行し、リアルタイムで経営数値を把握できます。',
        },
        {
          title: '給与計算・年末調整・法定調書',
          text: '毎月の給与計算や年末調整、法定調書までワンストップ対応。正確さはもちろん、業務効率化とコンプライアンス遵守を両立します。',
        },
        {
          title: '労務相談（社会保険労務士対応）',
          text: '就業規則の整備、働き方改革への対応、人事制度の見直しなど、労務全般の相談も可能です。税務・経理・労務を一体化させることで、安定した経営基盤をサポートします。',
        },
      ],
    },
    {
      title: '会社設立支援',
      icon: 'build',
      text: [
        '会社を設立する際には、登記や各種届出、規定作成など、数多くの手続きが必要です。',
        '私たちは、それらをワンストップで支援し、スムーズな立ち上げを実現します。',
      ],
      contents: [
        {
          title: '設立登記・各種届出',
          text: '司法書士や社労士と連携し、会社設立に必要な手続きを迅速かつ正確に進めます。',
        },
        {
          title: '社内規定作成',
          text: '就業規則や賃金規定など、会社経営に不可欠な規定をお客様の実情に合わせて整備します。',
        },
        {
          title: '節税シミュレーション',
          text: '設立前からシミュレーションを行い、法人化のメリット・デメリットを比較検討。将来を見据えた最適な選択をサポートします。',
        },
        {
          title: '設立後の継続支援',
          text: '設立直後の会計処理や税務対応も継続的にサポート。安定した事業基盤の構築を支援します。',
        },
      ],
    },
    {
      title: '組合業務',
      icon: 'groups',
      text: [
        '一般企業とは異なる会計・税務処理が必要となる「組合」にも対応しています。',
        '特に、投資事業有限責任組合（LPS）などの特殊なスキームにおいては、経験に基づいた的確な支援が欠かせません。',
      ],
      contents: [
        {
          title: '会計・経理業務の代行',
          text: '複雑な経理処理を専門家が代行し、透明性と正確性を担保します。',
        },
        {
          title: '税務関連のお知らせ・資料作成',
          text: '組合員向けのお知らせや税務関連資料を作成し、スムーズな情報共有をサポートします。',
        },
        {
          title: '特殊税制への対応',
          text: '組合特有の税務制度にも精通しているため、安心して業務をお任せいただけます。',
        },
      ],
    },
  ]

  const handleServiceClick = (service: any) => {
    setSelectedService(service)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedService(null)
  }

  return (
    <main className="mb-12 md:mb-20">
      <SectionHeader title="Tax Advisor" subtitle="企業の税務顧問">
        <p>
          法人・個人のお客様の多様なニーズに応えるため、税務・会計・労務を中心に幅広いサポートをご提供しています。
        </p>
        <p>
          専門家チームが一体となり、煩雑な事務手続きや複雑な税制に対してもスムーズな解決を実現します。
        </p>
      </SectionHeader>
      {/* CONTENT */}
      <section className="mt-12 md:mt-20">
        <ul className="grid gap-y-8 px-lg md:grid-cols-2 md:gap-12 xl:grid-cols-4">
          {services.map((a) => (
            <li
              key={a.title}
              onClick={() => handleServiceClick(a)}
              className="relative cursor-pointer rounded-lg border px-lg pb-10 pt-6 text-center border-body-15 hover:bg-primary-5 md:px-6 md:pb-12 md:pt-8"
            >
              <span className="material-symbols-outlined !text-[56px] text-primary md:!text-[64px]">
                {a.icon}
              </span>
              <h2 className="mt-fz-0.75 font-semibold leading-normal">
                {a.title}
              </h2>
              <div className="mt-3 grid gap-y-fz-0.75 text-left text-sm leading-loose text-body-65 md:mt-4">
                {a.text.map((b) => (
                  <p key={b} className="">
                    {b}
                  </p>
                ))}
              </div>
              <div className="absolute -bottom-0.25 -right-0.25 flex size-10 items-center justify-center rounded-lg bg-primary transition-colors">
                <span className="material-symbols-outlined !text-[24px] text-white">
                  add
                </span>
              </div>
            </li>
          ))}
        </ul>
      </section>
      <SectionContent title="私たちの強み" className="mt-12 md:mt-20">
        <ul className="grid gap-x-12 gap-y-6 md:grid-cols-3">
          {[
            {
              icon: 'workspace_premium',
              title: '専門性',
              text: (
                <>
                  法人税務から組合業務まで
                  <wbr />
                  幅広い分野をカバー
                </>
              ),
            },
            {
              icon: 'calculate',
              title: '効率性',
              text: (
                <>
                  クラウドツールを活用し、
                  <wbr />
                  迅速かつ正確な処理を実現
                </>
              ),
            },
            {
              icon: 'heart_check',
              title: '安心感',
              text: (
                <>
                  税理士・社労士・司法書士と連携した
                  <wbr />
                  ワンストップ体制
                </>
              ),
            },
          ].map((a) => (
            <li key={a.title} className="flex gap-x-4 md:block">
              <span className="material-symbols-outlined !text-[48px] text-primary">
                {a.icon}
              </span>
              <div>
                <p className="mt-1.5 text-sm font-semibold md:mt-fz-1 md:text-base">
                  {a.title}
                </p>
                <p className="mt-fz-0.75 break-keep text-sm leading-little-loose text-body-65">
                  {a.text}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </SectionContent>
      <SectionContent title="まずはご相談ください" className="mt-8 md:mt-12">
        <p className="-mt-fz-1 text-sm leading-loose md:-mt-fz-0.5 md:text-base">
          お客様の経営を支えるパートナーとして、長期的な視点でのサポートをお約束します。
        </p>
      </SectionContent>

      {/* Service Modal */}
      {selectedService && (
        <ServiceModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          service={selectedService}
        />
      )}
    </main>
  )
}
